import type {
  CreateTimesheetApprovalPayload,
  CreateTimesheetEntryPayload,
  CreateTimesheetPayload,
  CursorPaginatedResponse,
  Timesheet,
  TimesheetApproval,
  TimesheetApprovalListParams,
  TimesheetEntry,
  TimesheetEntryListParams,
  TimesheetFull,
  TimesheetListParams,
  UpdateTimesheetApprovalPayload,
  UpdateTimesheetEntryPayload,
  UpdateTimesheetPayload,
} from '@/libs/interfaces'

import {
  mockTimesheetApprovals,
  mockTimesheetEntries,
  mockTimesheets,
} from '../fixtures/timesheets'
import { delay, genID, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const timesheetStore = createEntityStore<Timesheet, 'timesheetID'>(mockTimesheets, 'timesheetID')
const timesheetEntryStore = createEntityStore<TimesheetEntry, 'timesheetEntryID'>(
  mockTimesheetEntries,
  'timesheetEntryID',
)
const timesheetApprovalStore = createEntityStore<TimesheetApproval, 'timesheetApprovalID'>(
  mockTimesheetApprovals,
  'timesheetApprovalID',
)

const filterTimesheets = (params: TimesheetListParams): Timesheet[] =>
  timesheetStore
    .getAll()
    .filter((timesheet) => (params.employeeID ? timesheet.employeeID === params.employeeID : true))
    .filter((timesheet) => (params.storeID ? timesheet.storeID === params.storeID : true))
    .filter((timesheet) => (params.status ? timesheet.status === params.status : true))

const filterTimesheetEntries = (params: TimesheetEntryListParams): TimesheetEntry[] =>
  timesheetEntryStore
    .getAll()
    .filter((entry) => (params.timesheetID ? entry.timesheetID === params.timesheetID : true))
    .filter((entry) => (params.activityType ? entry.activityType === params.activityType : true))

const filterTimesheetApprovals = (params: TimesheetApprovalListParams): TimesheetApproval[] =>
  timesheetApprovalStore
    .getAll()
    .filter((approval) => (params.timesheetID ? approval.timesheetID === params.timesheetID : true))
    .filter((approval) => (params.approverID ? approval.approverID === params.approverID : true))
    .filter((approval) =>
      params.status ? approval.timesheetApprovalsStatus === params.status : true,
    )

export const timesheetsApiMock = {
  create: async (orgID: string, payload: CreateTimesheetPayload): Promise<Timesheet> => {
    const timesheet: Timesheet = {
      timesheetID: genID('timesheet'),
      orgID,
      submittedBy: payload.employeeID,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload,
    }
    timesheetStore.insert(timesheet)
    return delay(timesheet)
  },

  list: async (params: TimesheetListParams): Promise<Timesheet[]> =>
    delay(filterTimesheets(params)),

  listCursor: async (params: TimesheetListParams): Promise<CursorPaginatedResponse<Timesheet>> =>
    delay(paginate(filterTimesheets(params), params)),

  getByID: async (_orgID: string, timesheetID: string): Promise<Timesheet> =>
    delay(timesheetStore.getById(timesheetID) ?? ({} as Timesheet)),

  getFull: async (_orgID: string, timesheetID: string): Promise<TimesheetFull> => {
    const timesheet = timesheetStore.getById(timesheetID) ?? ({} as Timesheet)
    const approval =
      timesheetApprovalStore.find((item) => item.timesheetID === timesheetID)[0] ?? null
    const entries = timesheetEntryStore.find((item) => item.timesheetID === timesheetID)
    return delay({ timesheet, approval, entries })
  },

  update: async (
    orgID: string,
    timesheetID: string,
    payload: UpdateTimesheetPayload,
  ): Promise<Timesheet> => {
    const updated = timesheetStore.update(timesheetID, {
      ...payload,
      orgID,
      updatedAt: nowISO(),
    })
    return delay(updated ?? ({ ...payload, timesheetID, orgID } as Timesheet))
  },

  delete: async (_orgID: string, timesheetID: string): Promise<Timesheet> => {
    const timesheet = timesheetStore.getById(timesheetID)
    timesheetStore.remove(timesheetID)
    return delay(timesheet ?? ({} as Timesheet))
  },
}

export const timesheetEntriesApiMock = {
  create: async (orgID: string, payload: CreateTimesheetEntryPayload): Promise<TimesheetEntry> => {
    const entry: TimesheetEntry = {
      timesheetEntryID: genID('timesheet-entry'),
      orgID,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload,
    }
    timesheetEntryStore.insert(entry)
    return delay(entry)
  },

  list: async (params: TimesheetEntryListParams): Promise<TimesheetEntry[]> =>
    delay(filterTimesheetEntries(params)),

  listCursor: async (
    params: TimesheetEntryListParams,
  ): Promise<CursorPaginatedResponse<TimesheetEntry>> =>
    delay(paginate(filterTimesheetEntries(params), params)),

  getByID: async (_orgID: string, timesheetEntryID: string): Promise<TimesheetEntry> =>
    delay(timesheetEntryStore.getById(timesheetEntryID) ?? ({} as TimesheetEntry)),

  update: async (
    orgID: string,
    timesheetEntryID: string,
    payload: UpdateTimesheetEntryPayload,
  ): Promise<TimesheetEntry> => {
    const updated = timesheetEntryStore.update(timesheetEntryID, {
      ...payload,
      orgID,
      updatedAt: nowISO(),
    })
    return delay(updated ?? ({ ...payload, timesheetEntryID, orgID } as TimesheetEntry))
  },

  delete: async (_orgID: string, timesheetEntryID: string): Promise<TimesheetEntry> => {
    const entry = timesheetEntryStore.getById(timesheetEntryID)
    timesheetEntryStore.remove(timesheetEntryID)
    return delay(entry ?? ({} as TimesheetEntry))
  },
}

export const timesheetApprovalsApiMock = {
  create: async (
    orgID: string,
    payload: CreateTimesheetApprovalPayload,
  ): Promise<TimesheetApproval> => {
    const approval: TimesheetApproval = {
      timesheetApprovalID: genID('timesheet-approval'),
      orgID,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload,
    }
    timesheetApprovalStore.insert(approval)
    return delay(approval)
  },

  list: async (params: TimesheetApprovalListParams): Promise<TimesheetApproval[]> =>
    delay(filterTimesheetApprovals(params)),

  listCursor: async (
    params: TimesheetApprovalListParams,
  ): Promise<CursorPaginatedResponse<TimesheetApproval>> =>
    delay(paginate(filterTimesheetApprovals(params), params)),

  getByID: async (_orgID: string, timesheetApprovalID: string): Promise<TimesheetApproval> =>
    delay(timesheetApprovalStore.getById(timesheetApprovalID) ?? ({} as TimesheetApproval)),

  update: async (
    orgID: string,
    timesheetApprovalID: string,
    payload: UpdateTimesheetApprovalPayload,
  ): Promise<TimesheetApproval> => {
    const updated = timesheetApprovalStore.update(timesheetApprovalID, {
      ...payload,
      orgID,
      updatedAt: nowISO(),
    })
    return delay(updated ?? ({ ...payload, timesheetApprovalID, orgID } as TimesheetApproval))
  },

  delete: async (_orgID: string, timesheetApprovalID: string): Promise<TimesheetApproval> => {
    const approval = timesheetApprovalStore.getById(timesheetApprovalID)
    timesheetApprovalStore.remove(timesheetApprovalID)
    return delay(approval ?? ({} as TimesheetApproval))
  },
}
